import React from 'react';
import { Navigate } from 'react-router-dom';
import usePortalAccess from '@/access/usePortalAccess';
import ProtectedRoute from '@/components/ProtectedRoute';
import useCoreLoopConfigAccess from '@/hooks/useCoreLoopConfigAccess';
import { useAuth } from '@/contexts/AuthContext';
import {
  getDefaultHomePath,
  getRouteCoreLoopConfigAccess,
  getRoutePermissionAccess,
  isRouteAccessible,
  type PortalRouteDef,
} from '@/config/portalRoutes.config';

type PortalProtectedRouteProps = {
  route: PortalRouteDef;
  children: React.ReactNode;
};

const PortalProtectedRoute = ({
  route,
  children,
}: PortalProtectedRouteProps) => {
  const access = usePortalAccess();
  const coreLoopConfig = useCoreLoopConfigAccess();
  const { isAuthenticated } = useAuth();

  if (isAuthenticated && !isRouteAccessible(route, access)) {
    return <Navigate to={getDefaultHomePath(access)} replace />;
  }

  if (isAuthenticated) {
    const permissionAccess = getRoutePermissionAccess(route, access);
    const configAccess = getRouteCoreLoopConfigAccess(route, coreLoopConfig);

    if (permissionAccess === 'denied' || configAccess === 'denied') {
      return <Navigate to={getDefaultHomePath(access)} replace />;
    }

    // Wait for permissions and configuration rather than bouncing a deep link.
    if (permissionAccess === 'pending' || configAccess === 'pending') {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-t-2 border-b-2 border-blue-500"></div>
        </div>
      );
    }
  }

  return <ProtectedRoute>{children}</ProtectedRoute>;
};

export default PortalProtectedRoute;
